import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CurrencyPipe, isPlatformBrowser } from '@angular/common';
import {
  Component,
  computed,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActiveBookingIntentService,
  ActiveProviderService,
  BookingIntentsService,
  ToastService,
} from '@services';
import {
  BookAppointmentPatientDialogComponent,
  ConfirmDialogComponent,
  DisclaimersDialogComponent,
  DisplayAvatarComponent,
  SpinnerComponent,
} from '@ui-lib';
import { catchError, EMPTY, of, switchMap, take, tap } from 'rxjs';

export interface CheckoutDialogData {
  servicesCost?: number | null;
  totalDiscount?: number | null;
  subtotalCents?: number;
  taxAmountCents?: number;
  totalCents?: number;
  taxPercentage?: number | null;
  checkoutUrl: string;
  bookingIntentId?: string;
}

@Component({
  selector: 'icaredx-checkout-dialog',
  imports: [DisplayAvatarComponent, CurrencyPipe, SpinnerComponent],
  templateUrl: './checkout-dialog.component.html',
})
export class CheckoutDialogComponent {
  private readonly activeProvider = inject(ActiveProviderService);
  private readonly dialog = inject(Dialog);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly dialogRef = inject(DialogRef);
  private readonly bookingIntentsService = inject(BookingIntentsService);
  private readonly activeIntentService = inject(ActiveBookingIntentService);
  private readonly toast = inject(ToastService);

  readonly data: CheckoutDialogData = inject(DIALOG_DATA);

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  submitting = signal(false);

  provider = toSignal(this.activeProvider.getActiveProviderData());

  readonly hasBreakdown = computed(
    () =>
      this.data.subtotalCents != null &&
      this.data.taxAmountCents != null &&
      this.data.totalCents != null
  );

  readonly subtotalDollars = computed(
    () => (this.data.subtotalCents ?? 0) / 100
  );

  readonly totalDiscountDollars = computed(() => this.data.totalDiscount ?? 0);

  readonly taxAmountDollars = computed(
    () => (this.data.taxAmountCents ?? 0) / 100
  );

  readonly taxLabel = computed(() => {
    const taxPercentage = this.data.taxPercentage;

    return taxPercentage != null ? `Tax (${taxPercentage}%)` : 'Tax';
  });

  readonly totalDollars = computed(() => (this.data.totalCents ?? 0) / 100);

  readonly hasDiscount = computed(() => this.totalDiscountDollars() > 0);

  readonly discountedServicesCost = computed(
    () => (this.data.servicesCost ?? 0) - this.totalDiscountDollars()
  );

  get providerNameSplited() {
    const [givenName, familyName] = this.provider()!.providerName.split(' ');

    return {
      givenName: givenName,
      familyName: familyName,
    };
  }

  openDisclaimer(type: 'privacyPolicy' | 'termsOfService') {
    if (this.isBrowser) {
      this.dialog.open(DisclaimersDialogComponent, {
        data: { disclaimerType: type },
      });
    }
  }

  submit() {
    this.submitting.set(true);
    window.location.href = this.data.checkoutUrl;
  }

  cancelBooking(): void {
    const intentId = this.data.bookingIntentId;
    if (!intentId) return;

    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Cancel this booking?',
          message:
            "Canceling will release your held appointment slot. You'll be able to start a new booking immediately.",
          confirmButtonLabel: 'Yes, cancel booking',
          confirmButtonColor: 'bg-red-700 hover:bg-red-800 focus:ring-red-300',
          onConfirm: () => of(undefined as void),
        },
      })
      .closed.pipe(
        take(1),
        switchMap(confirmed => {
          if (!confirmed) return EMPTY;
          return this.bookingIntentsService.cancelBookingIntent(intentId).pipe(
            tap(() => {
              this.dialogRef.close();
              this.activeIntentService.reload();
              this.dialog.open(BookAppointmentPatientDialogComponent);
            }),
            catchError(err => {
              this.toast.showWarning(resolveCancelErrorMessage(err));
              this.dialogRef.close();
              this.activeIntentService.reload();
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  }
}

function resolveCancelErrorMessage(err: unknown): string {
  const e = err as { status?: number; error?: { code?: string } };
  if (e?.status === 422 && e?.error?.code === 'INVALID_BOOKING_STATUS') {
    return 'This booking has already been confirmed or is no longer active.';
  }
  if (e?.status === 404) {
    return 'Booking not found. It may have already expired.';
  }
  return 'Unable to cancel booking. Please try again.';
}
